import { useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  FormControlLabel, IconButton, InputAdornment, MenuItem, Paper, Stack, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  lasaApi, type LasaPairDto, type LasaPairPayload, type ItemDto,
  type ConfusionType, type LasaSeverity,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { ItemPicker } from "@/components/Pickers";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import { PageHeader } from "@/components/PageHeader";

const CONFUSION_TYPES: { value: ConfusionType; label: string }[] = [
  { value: "SoundAlike", label: "Sound-alike" },
  { value: "LookAlike", label: "Look-alike" },
  { value: "Both", label: "Both" },
];
const SEVERITIES: LasaSeverity[] = ["Low", "Medium", "High"];
const SEV_COLOR: Record<LasaSeverity, "default" | "warning" | "error"> = {
  Low: "default", Medium: "warning", High: "error",
};

export function LasaPairsPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<LasaPairDto | null>(null);
  const canManage = useAuth(s => s.hasPermission("catalog.lasa.manage"));

  const query = useQuery({
    queryKey: ["lasa-pairs", search],
    queryFn: () => lasaApi.list({ search: search || undefined, pageSize: 300 }),
  });

  const deleteMutation = useMutation({
    meta: { successMessage: "LASA pair removed" },
    mutationFn: (id: string) => lasaApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lasa-pairs"] }),
  });

  const rows = query.data?.items ?? [];

  return (
    <Box>
      <PageHeader
        breadcrumbs={["Catalog"]}
        title="Look-alike / sound-alike (LASA)"
        actions={
          canManage ? (
            <Button variant="contained" startIcon={<AddIcon />}
              onClick={() => { setEditing(null); setDialogOpen(true); }}>
              New LASA pair
            </Button>
          ) : undefined
        }
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        Flag pairs of items that are dangerously easy to confuse. High-severity pairs force a
        confirmation at the billing counter; Low/Medium show a warning.
      </Alert>

      <Paper sx={{ p: 2 }}>
        <TextField placeholder="Search by item name or SKU" fullWidth size="small" value={search}
          onChange={(e) => setSearch(e.target.value)} sx={{ mb: 2 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {deleteMutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(deleteMutation.error)}</Alert>}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item A</TableCell>
                <TableCell>Item B</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="center">Severity</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="center">Status</TableCell>
                {canManage && <TableCell align="right" width={96}>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.id} hover>
                  <TableCell><b>{r.itemASku}</b> · {r.itemAName}</TableCell>
                  <TableCell><b>{r.itemBSku}</b> · {r.itemBName}</TableCell>
                  <TableCell>{CONFUSION_TYPES.find(t => t.value === r.confusionType)?.label ?? r.confusionType}</TableCell>
                  <TableCell align="center"><Chip size="small" color={SEV_COLOR[r.severity]} label={r.severity} /></TableCell>
                  <TableCell>{r.notes ?? "—"}</TableCell>
                  <TableCell align="center">
                    <Chip size="small" color={r.isActive ? "success" : "default"} label={r.isActive ? "Active" : "Inactive"} />
                  </TableCell>
                  {canManage && (
                    <TableCell align="right">
                      <Tooltip title="Edit"><IconButton size="small"
                        onClick={() => { setEditing(r); setDialogOpen(true); }}>
                        <EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" color="error"
                        onClick={() => { if (confirm("Remove this LASA pair?")) deleteMutation.mutate(r.id); }}>
                        <DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {rows.length === 0 && !query.isLoading && (
                <TableRow><TableCell colSpan={7}>
                  <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                    No LASA pairs defined yet.
                  </Typography>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <LasaDialog open={dialogOpen} onClose={() => setDialogOpen(false)} row={editing} />
    </Box>
  );
}

function LasaDialog({ open, onClose, row }: { open: boolean; onClose: () => void; row: LasaPairDto | null }) {
  const qc = useQueryClient();
  const isEdit = !!row;
  const [itemA, setItemA] = useState<ItemDto | null>(null);
  const [itemB, setItemB] = useState<ItemDto | null>(null);
  const [confusionType, setConfusionType] = useState<ConfusionType>("SoundAlike");
  const [severity, setSeverity] = useState<LasaSeverity>("Medium");
  const [notes, setNotes] = useState("");
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (!open) return;
    setItemA(null); setItemB(null);
    setConfusionType(row?.confusionType ?? "SoundAlike");
    setSeverity(row?.severity ?? "Medium");
    setNotes(row?.notes ?? "");
    setIsActive(row?.isActive ?? true);
  }, [open, row]);

  const mutation = useMutation({
    meta: { successMessage: isEdit ? "LASA pair updated" : "LASA pair created" },
    mutationFn: async () => {
      const payload: LasaPairPayload = isEdit
        ? { confusionType, severity, notes: notes || null, isActive }
        : { itemAId: itemA?.id, itemBId: itemB?.id, confusionType, severity, notes: notes || null };
      if (isEdit && row) return lasaApi.update(row.id, payload);
      return lasaApi.create(payload);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["lasa-pairs"] }); onClose(); },
  });

  const canSubmit = isEdit || (!!itemA && !!itemB && itemA.id !== itemB.id);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <ClosableDialogTitle onClose={onClose}>{isEdit ? "Edit LASA pair" : "New LASA pair"}</ClosableDialogTitle>
      <DialogContent dividers>
        {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
        <Stack spacing={2}>
          {isEdit ? (
            <TextField label="Pair" size="small" fullWidth disabled
              value={`${row!.itemASku} · ${row!.itemAName}  ⇄  ${row!.itemBSku} · ${row!.itemBName}`} />
          ) : (
            <>
              <ItemPicker value={itemA} onChange={setItemA} label="Item A" required />
              <ItemPicker value={itemB} onChange={setItemB} label="Item B" required
                error={!!itemA && !!itemB && itemA.id === itemB.id}
                helperText={!!itemA && !!itemB && itemA.id === itemB.id ? "Must be a different item" : undefined} />
            </>
          )}
          <Stack direction="row" spacing={2}>
            <TextField select label="Confusion type" size="small" fullWidth
              value={confusionType} onChange={(e) => setConfusionType(e.target.value as ConfusionType)}>
              {CONFUSION_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
            <TextField select label="Severity" size="small" fullWidth
              value={severity} onChange={(e) => setSeverity(e.target.value as LasaSeverity)}
              helperText="High forces billing confirmation">
              {SEVERITIES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Stack>
          <TextField label="Notes (optional)" size="small" fullWidth multiline minRows={2}
            value={notes} onChange={(e) => setNotes(e.target.value)} />
          {isEdit && (
            <FormControlLabel label="Active"
              control={<Switch checked={isActive} onChange={(_, v) => setIsActive(v)} />} />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit || mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}
          onClick={() => mutation.mutate()}>
          {isEdit ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
